import type { AiLog, ProjectState } from "./types";

const DB_NAME = "scholarflow-local";
const DB_VERSION = 1;
const PROJECTS = "projects";
const LOGS = "logs";
const HANDLES = "handles";
const ACTIVE_PROJECT_KEY = "scholarflow.activeProject";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PROJECTS)) {
        database.createObjectStore(PROJECTS, { keyPath: "id" });
      }
      if (!database.objectStoreNames.contains(LOGS)) {
        const store = database.createObjectStore(LOGS, { keyPath: "id" });
        store.createIndex("projectId", "projectId", { unique: false });
        store.createIndex("startedAt", "startedAt", { unique: false });
      }
      if (!database.objectStoreNames.contains(HANDLES)) {
        database.createObjectStore(HANDLES);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionRequest<T>(
  storeName: string,
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDatabase().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(storeName, mode);
        const request = operation(transaction.objectStore(storeName));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.oncomplete = () => database.close();
      }),
  );
}

export async function saveProject(project: ProjectState): Promise<void> {
  await transactionRequest(PROJECTS, "readwrite", (store) => store.put(project));
  localStorage.setItem(ACTIVE_PROJECT_KEY, project.id);
}

export async function loadActiveProject(): Promise<ProjectState | undefined> {
  const id = localStorage.getItem(ACTIVE_PROJECT_KEY);
  if (!id) return undefined;
  return transactionRequest<ProjectState | undefined>(
    PROJECTS,
    "readonly",
    (store) => store.get(id),
  );
}

export async function appendAiLog(log: AiLog): Promise<void> {
  await transactionRequest(LOGS, "readwrite", (store) => store.put(log));
}

export async function listAiLogs(projectId: string): Promise<AiLog[]> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(LOGS, "readonly");
    const index = transaction.objectStore(LOGS).index("projectId");
    const request = index.getAll(projectId);
    request.onsuccess = () =>
      resolve(
        request.result.sort((a, b) => a.startedAt.localeCompare(b.startedAt)),
      );
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => database.close();
  });
}

export async function saveDirectoryHandle(
  projectId: string,
  handle: FileSystemDirectoryHandle,
): Promise<void> {
  await transactionRequest(HANDLES, "readwrite", (store) =>
    store.put(handle, projectId),
  );
}

export async function loadDirectoryHandle(
  projectId: string,
): Promise<FileSystemDirectoryHandle | undefined> {
  return transactionRequest<FileSystemDirectoryHandle | undefined>(
    HANDLES,
    "readonly",
    (store) => store.get(projectId),
  );
}

export async function clearScholarFlowLocalData(): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve();
  });
  localStorage.removeItem(ACTIVE_PROJECT_KEY);
  localStorage.removeItem("scholarflow.aiConfig");
  localStorage.removeItem("scholarflow.apiKey.persisted");
  localStorage.removeItem("scholarflow.uiScale");
  localStorage.removeItem("scholarflow.onboardingDismissed");
  sessionStorage.removeItem("scholarflow.apiKey");
}

export function downloadText(
  filename: string,
  content: string,
  type = "text/plain;charset=utf-8",
) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
